import { useState } from "react";
import * as Sentry from "@sentry/react";
import type { User } from "firebase/auth";
import { ClientLawyerData } from "../redux/client-case/activeClientSlice";
import { LawyerState } from "../redux/lawyer/lawyerSlice";
import { useLogOut } from "./useLogOut";
import { SessionAPI, SessionMeResponseDTO } from "../api/sessionApi";
import { APIError } from "../api/utils/errors";
import type { OnboardingStatus } from "../features/onboarding/types";

type LawyerSubscription = LawyerState["subscription"];
type LawyerEntitlements = LawyerState["entitlements"];

const normalizeEntitlements = (raw: unknown): LawyerEntitlements => {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.features)) return undefined;
  return {
    planType: typeof r.planType === "string" ? r.planType : null,
    features: r.features.filter((f): f is string => typeof f === "string")
  };
};

const normalizeSubscription = (
  raw: unknown
): LawyerSubscription | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const { status } = r;
  const normalizedStatus =
    status === "paid" || status === "free_trial" || status === "freemium"
      ? status
      : undefined;
  if (!normalizedStatus) return undefined;
  const out: LawyerSubscription = { status: normalizedStatus };
  if (typeof r.stripeCustomerId === "string")
    out.stripeCustomerId = r.stripeCustomerId;
  if (typeof r.planId === "string") out.planId = r.planId;
  if (typeof r.remainingCases === "number")
    out.remainingCases = r.remainingCases;
  if (typeof r.request === "boolean") out.request = r.request;
  return out;
};

const ONBOARDING_STATUSES: readonly OnboardingStatus[] = [
  "pending",
  "in_progress",
  "completed",
  "dismissed"
];

const onboardingStatusOf = (raw: unknown): OnboardingStatus | null =>
  ONBOARDING_STATUSES.find((s) => s === raw) ?? null;

export const lawyerStateFromSession = (
  session: SessionMeResponseDTO
): LawyerState | null => {
  const { lawyer } = session;
  if (!lawyer?.uid) return null;
  return {
    firstName: lawyer.firstName || "",
    middleName: lawyer.middleName || "",
    lastName: lawyer.lastName || "",
    pronouns: lawyer.pronouns || "",
    title: lawyer.title || "",
    email: lawyer.email || "",
    address: lawyer.address || "",
    state: lawyer.state || "",
    city: lawyer.city || "",
    zipcode: lawyer.zipcode || "",
    phone: lawyer.phone || "",
    practiceName: lawyer.practiceName || "",
    lawFirm: lawyer.lawFirm || "",
    law_firm_id: lawyer.law_firm_id ?? null,
    time_tracking_enabled: Boolean(lawyer.time_tracking_enabled),
    legacyDrafting: Boolean(lawyer.legacyDrafting),
    website: lawyer.website || "",
    avatarName: session.avatar?.avatarDataUrl ? "avatar" : "",
    avatarSrc: session.avatar?.avatarDataUrl || "",
    uid: lawyer.uid,
    role: lawyer.role || "",
    subscription: normalizeSubscription(lawyer.subscription) || {
      status: "free_trial",
      request: false
    },
    entitlements: normalizeEntitlements(session.entitlements),
    hasAcceptedTerms: Boolean(lawyer.hasAcceptedTerms),
    hideQualityReview: Boolean(lawyer.hideQualityReview),
    betaFeatures: Array.isArray(lawyer.betaFeatures) ? lawyer.betaFeatures : [],
    hiddenPrompts: Array.isArray(lawyer.hiddenPrompts)
      ? lawyer.hiddenPrompts
      : [],
    exhibitListFormat: lawyer.exhibitListFormat ?? null,
    organizeInstruction: lawyer.organizeInstruction ?? "",
    documentTitleStyle: lawyer.documentTitleStyle ?? "",
    subagentModel: lawyer.subagentModel ?? "",
    subagentThinking: Boolean(lawyer.subagentThinking),
    onboardingStatus: onboardingStatusOf(lawyer.onboarding?.status)
  };
};

export const useGetLawyerData = () => {
  const [isLoadingGetLawyerData, setIsLoadingGetLawyerData] = useState(false);
  const [isNoLawyerData, setIsNoLawyerData] = useState(false);
  const [lawyerData, setLawyerData] = useState<ClientLawyerData | null>(null);
  const [fullLawyerData, setFullLawyerData] = useState<LawyerState | null>(
    null
  );
  const { onSubmitLogOut } = useLogOut();

  const onSubmitGetLawyerData = async (
    lawyerId: string,
    opts?: {
      logoutOnMissing?: boolean;
      throwOnError?: boolean;
      tokenUser?: User | null;
    }
  ): Promise<boolean> => {
    setIsLoadingGetLawyerData(true);
    setIsNoLawyerData(false);
    setLawyerData(null);
    setFullLawyerData(null);
    try {
      const tokenUser = opts?.tokenUser ?? null;

      const getSession = async (forceRefresh: boolean) => {
        const token = forceRefresh
          ? await tokenUser?.getIdToken(true)
          : await tokenUser?.getIdToken();
        return await SessionAPI.getMe({ token: token ?? null });
      };

      let session;
      try {
        session = await getSession(false);
      } catch (e) {
        if (
          e instanceof APIError &&
          e.status === 403 &&
          /authorization header is missing or invalid/i.test(e.message) &&
          tokenUser
        ) {
          session = await getSession(true);
        } else {
          throw e;
        }
      }

      const { lawyer } = session;
      const fullData = lawyerStateFromSession(session);
      if (!lawyer || !lawyer.uid || lawyer.uid !== lawyerId || !fullData) {
        setIsNoLawyerData(true);
        if (opts?.logoutOnMissing !== false) onSubmitLogOut("profile_missing");
        return false;
      }

      setLawyerData({
        lawyerEmail: lawyer.email || "",
        lawyerFirstName: lawyer.firstName || "",
        lawyerLastName: lawyer.lastName || ""
      });

      setFullLawyerData(fullData);

      return true;
    } catch (e) {
      if (e instanceof APIError && e.status === 404) {
        setIsNoLawyerData(true);
        if (opts?.logoutOnMissing !== false) onSubmitLogOut("profile_missing");
        return false;
      }
      Sentry.captureException(e);
      if (opts?.throwOnError) throw e;
      return false;
    } finally {
      setIsLoadingGetLawyerData(false);
    }
  };

  return {
    isLoadingGetLawyerData,
    lawyerData,
    fullLawyerData,
    onSubmitGetLawyerData,
    isNoLawyerData
  };
};
